import PrettyDate from '@/components/PrettyDate';
import { MailThread } from '@/typings/models/Email';
import { F } from '@/utils/intl';
import {
  InboxOutlined,
  PaperClipOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Empty, List, Skeleton, Spin, Typography } from 'antd';
import classNames from 'classnames';
import React from 'react';
import styles from './EmailList.less';

const { Text } = Typography;

export const threadKey = (thread: MailThread) =>
  `${thread.mailbox_id}|${thread.conversation_id}`;

interface EmailListProps {
  threads: MailThread[];
  loading: boolean;
  /** Deadline-bounded fan-out returned partial results; a poll is running. */
  incomplete?: boolean;
  pendingMailboxes?: number;
  /** Mailboxes the server could not search (e.g. excluded by the tenant's access policy). */
  failedMailboxes?: string[];
  /**
   * Whether rows should say which mailbox holds the thread. Only meaningful
   * when the result set spans more than one mailbox — a constant caption on
   * every row is noise, so the parent computes this from the data.
   */
  showMailbox?: boolean;
  /** Overrides the no-threads message (e.g. while a search filter is active). */
  emptyText?: React.ReactNode;
  selectedKey?: string | null;
  onSelect: (thread: MailThread) => void;
}

/**
 * Thread list for one lead — every Outlook conversation the lead's address
 * appears in, across the enabled monitored mailboxes.
 *
 * Row anatomy (top to bottom): subject + time, preview + attachment marker,
 * then (only when it disambiguates) the holding mailbox. The thread model
 * carries no counterparty identity, so rows deliberately show no sender
 * name/avatar rather than faking one — the viewer has the real senders.
 */
const EmailList: React.FC<EmailListProps> = ({
  threads,
  loading,
  incomplete,
  pendingMailboxes,
  failedMailboxes,
  showMailbox,
  emptyText,
  selectedKey,
  onSelect,
}) => {
  if (loading && threads.length === 0) {
    // Skeleton rows in the list's own anatomy — a centered spinner gives no
    // hint of what is coming and makes the pane collapse to zero height.
    return (
      <div className={styles.threadList}>
        {[0, 1, 2, 3, 4].map((row) => (
          <div key={row} className={styles.skeletonRow}>
            <Skeleton
              active
              title={{ width: '55%' }}
              paragraph={{ rows: 1, width: '85%' }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (!loading && threads.length === 0 && !incomplete) {
    return (
      <div className={styles.emptyWrap}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            emptyText ?? (
              <F
                id="email.threads.empty"
                defaultMessage="No email communication yet"
              />
            )
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.threadList}>
      {incomplete && (
        <div className={styles.searchingBanner}>
          <Spin size="small" />
          <Text type="secondary">
            <F
              id="email.threads.still_searching"
              defaultMessage="Still searching {count} mailboxes…"
              values={{ count: pendingMailboxes }}
            />
          </Text>
        </div>
      )}
      {!!failedMailboxes?.length && (
        <div
          className={classNames(styles.searchingBanner, styles.failedBanner)}
        >
          <Text type="warning">
            <F
              id="email.threads.failed_mailboxes"
              defaultMessage="Not searchable: {mailboxes}"
              values={{ mailboxes: failedMailboxes.join(', ') }}
            />
          </Text>
        </div>
      )}
      <List
        dataSource={threads}
        rowKey={threadKey}
        renderItem={(thread) => (
          <List.Item
            className={classNames(
              styles.threadItem,
              selectedKey === threadKey(thread) && styles.threadItemSelected,
            )}
            onClick={() => onSelect(thread)}
          >
            <div className={styles.threadRow}>
              <div className={styles.threadHeader}>
                <Text strong ellipsis className={styles.threadSubject}>
                  {thread.subject || (
                    <F id="common.no_subject" defaultMessage="(No Subject)" />
                  )}
                </Text>
                {thread.message_count > 1 && (
                  <span className={styles.countChip}>
                    {thread.message_count}
                  </span>
                )}
                {thread.last_message_at && (
                  <Text type="secondary" className={styles.threadDate}>
                    <PrettyDate date={thread.last_message_at} />
                  </Text>
                )}
              </div>
              <div className={styles.previewRow}>
                <Text
                  type="secondary"
                  ellipsis
                  className={styles.threadPreview}
                >
                  {thread.last_message_preview}
                </Text>
                {thread.has_attachments && (
                  <PaperClipOutlined className={styles.attachmentIcon} />
                )}
              </div>
              {showMailbox && (
                <div className={styles.mailboxRow}>
                  {thread.mailbox_kind === 'shared' ? (
                    <TeamOutlined className={styles.mailboxIcon} />
                  ) : (
                    <InboxOutlined className={styles.mailboxIcon} />
                  )}
                  <Text
                    type="secondary"
                    ellipsis
                    className={styles.mailboxAddress}
                  >
                    {thread.mailbox_address}
                  </Text>
                </div>
              )}
            </div>
          </List.Item>
        )}
      />
    </div>
  );
};

export default EmailList;
